/**
 * Borrows heavily from the original, now deperecated, NPM package https://npmjs.com/package/dager,
 * Converted to typescript
 *
 * Original License
 * Copyright (c) 2015, Santiago Pelufo <>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 */

import { Edge, Elements } from "Types/Journey";
import { isEdge } from "Utils/JourneyUtils";

export class DAGCyclicError extends Error {
    constructor(msg: string) {
        super(msg);

        // Set the prototype explicitly. (explanation at link below)
        //https://github.com/Microsoft/TypeScript-wiki/blob/main/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
        Object.setPrototypeOf(this, DAGCyclicError.prototype);
    }
}

export type DAGState = {
    forward: any;
    backward: any;
    nodes: any;
};

export function node(dag: DAGState, node: any, nodeValue?: any) {
    if (!(node in dag.nodes)) {
        dag.forward[node] = {};
        dag.backward[node] = {};
    }
    return (dag.nodes[node] = nodeValue);
}

function cycled(dag: DAGState, node: string): boolean {
    const check = (source: string) => {
        for (const target in dag.forward[source]) {
            if (dag.forward[target][node] || check(target)) {
                return true;
            }
        }
        return false;
    };

    return check(node);
}

function cycleForward(dag: DAGState, source: string, forwardNodes: Set<string>) {
    for (const target in dag.forward[source]) {
        forwardNodes.add(target);
        cycleForward(dag, target, forwardNodes);
    }
}

export function edge(dag: DAGState, source: string, target: string, edgeValue?: string) {
    if (typeof source !== "string" || typeof target !== "string") {
        throw new TypeError("Nodes must be strings.");
    }

    if (source === target) {
        throw new Error("Source and target must be different.");
    }

    if (!(source in dag.nodes)) {
        node(dag, source);
    }
    if (!(target in dag.nodes)) {
        node(dag, target);
    }

    dag.forward[source][target] = edgeValue;
    dag.backward[target][source] = edgeValue;

    // If a cycle was just introduced in the graph,
    // revert the insertion and throw an exception.
    if (cycled(dag, source)) {
        delete dag.forward[source][target];
        delete dag.backward[target][source];
        throw new DAGCyclicError("Failed DAG add. No cycles allowed.");
    }
}

/* New functions to support journey builder flow */
export function addEdge(dag: DAGState, source: string, target: string, edgeValue?: string): boolean {
    /*
        Attemp to add an edge to this dag, and returns true or false based on
        wheter or not edge has been succesfully added to the DAG
    */
    try {
        edge(dag, source, target, edgeValue);
        return true;
    } catch {
        return false;
    }
}

export function removeEdge(dag: DAGState, source: string, target: string): DAGState {
    delete dag.forward[source][target];
    delete dag.backward[target][source];
    return dag;
}

export function removeEdges(dag: DAGState, elements: Array<Edge>): DAGState {
    elements.forEach((el) => {
        if (isEdge(el)) {
            const { source, target } = el;
            removeEdge(dag, source, target);
        }
    });
    return dag;
}

export function buildDAG(elements: Elements): DAGState {
    const dag = {
        forward: {},
        backward: {},
        nodes: {},
    };

    /*
        Create A DAG from all edges in a flow
    */
    elements.forEach((el) => {
        if (isEdge(el)) {
            edge(dag, el.source, el.target, el.id);
        }
    });

    return dag;
}

export function get(dag: DAGState, source: string, target?: string) {
    return typeof target === "string" ? dag.forward[source] && dag.forward[source][target] : dag.nodes[source];
}

export function from(dag: DAGState, source: string) {
    return dag.forward[source];
}

export function to(dag: DAGState, target: string) {
    return dag.backward[target];
}

export function allFrom(dag: DAGState, source: string): Set<string> {
    const forwardNodes = new Set<string>([source]);
    cycleForward(dag, source, forwardNodes);

    return forwardNodes;
}
